import { Button, Flex } from '@utilitywarehouse/hearth-react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ErrorIndicator } from '@components/ErrorIndicator';
import { Layout } from '@components/Layout';
import { DISCOUNT_IDS } from '@constants/apiConstants';
import { paths } from '@constants/paths';
import BhcPromoBanner from '@features/BoilerHomeCover/BHCPromoBanner';
import { BundleTile } from '@features/Bundle/BundleTile';
import { ProductSubtype, ProductType } from '@generated/gql/graphql';
import { useBundleBasket } from '@hooks/useBundleBasket';
import { useDetectMidJourneyLogin } from '@hooks/useDetectMidJourneyLogin';
import { usePageStateHandler } from '@hooks/usePageStateHandler';
import { withProfiler } from '@services/sentry/withProfiler';
import { getInsuranceRedirectPath } from '@utils/insurance';
import { calculateDiscountedPrice } from '@utils/price';

import './styles.scss';

const PAGE_TITLE = 'Insurance';

const InsuranceUnwrapped = () => {
  const navigate = useNavigate();
  const { basket, error, loading, basketData } = useBundleBasket({});

  useDetectMidJourneyLogin({ basketData, loading });
  const { isLoading, hasError } = usePageStateHandler({
    loading,
    hasData: !!basket,
    error,
  });

  const boilerInsurance = basket?.insurance.boilerInsurance;
  const boilerAvailable = !!boilerInsurance?.available;

  const incomeProtector = basket?.insurance.incomeProtector;
  const incomeAvailable = !!incomeProtector?.available;

  const anyInsuranceAvailable = boilerAvailable || incomeAvailable;

  const bhcPromoDiscount =
    boilerInsurance?.discount?.id ===
      DISCOUNT_IDS.INSURANCE_BOILER_THREE_MONTHS_FREE_DISCOUNT &&
    boilerInsurance.discount.qualified &&
    boilerInsurance.quote
      ? {
          price: calculateDiscountedPrice(
            boilerInsurance.quote.totalMonthlyCost,
            boilerInsurance.discount
          ),
          durationMonths: boilerInsurance.discount.creditFrequency ?? 0,
        }
      : null;

  if (isLoading) {
    return null;
  }

  if (!anyInsuranceAvailable) {
    return <Navigate to={paths.bundle} />;
  }

  const redirectPath = getInsuranceRedirectPath({
    boilerAvailable,
    incomeAvailable,
  });

  if (redirectPath) {
    return <Navigate to={redirectPath} />;
  }

  if (hasError) {
    return (
      <Layout
        title={PAGE_TITLE}
        onBack={() => navigate(paths.bundle)}
        backButtonLabel="Back to bundle"
      >
        <ErrorIndicator />
      </Layout>
    );
  }
  return (
    <Layout
      title={PAGE_TITLE}
      onBack={() => navigate(paths.bundle)}
      backButtonLabel="Back to bundle"
    >
      <Flex
        direction="column"
        spacing="2xl"
        className="wrapper insurance__Content"
      >
        <Flex direction="column" spacing="xl">
          {bhcPromoDiscount && <BhcPromoBanner />}
          {boilerAvailable && (
            <BundleTile
              productType={ProductType.Insurance}
              subType={ProductSubtype.BoilerHomeCover}
              price={boilerInsurance.monthlyCost}
              isSelected={boilerInsurance.selected}
              compact
              showMonthLabel
              discount={bhcPromoDiscount}
            />
          )}
          {incomeAvailable && (
            <BundleTile
              productType={ProductType.Insurance}
              subType={ProductSubtype.IncomeProtector}
              price={incomeProtector.monthlyCost}
              isSelected={incomeProtector.selected}
              compact
            />
          )}
        </Flex>
        <Flex
          direction={{
            tablet: 'row',
            mobile: 'column',
          }}
          justifyContent="end"
          className="insurance__Save"
        >
          <Button
            data-testid="insurance-save"
            onClick={() => navigate(paths.bundle)}
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </Layout>
  );
};

const Insurance = withProfiler(InsuranceUnwrapped);
export { Insurance };
